import { useState, useEffect, useRef, useCallback } from 'react';
import { useNode } from '../contexts/NodeContext';
import { useAuth } from '../contexts/AuthContext';
import {
  ConfirmDialog,
  PageTitle, PAGE_FILL_H,
  AlertRuleModal, AlertRulesList, ruleTitle, conditionsProblem,
  type EventCatalogEntry,
} from '@trinetra/shared';
import {
  getNodeAlertConfigs,
  createNodeAlertConfig,
  updateNodeAlertConfig,
  toggleNodeAlertConfig,
  duplicateNodeAlertConfig,
  deleteNodeAlertConfig,
  getNodeCameras,
  getNodeGroups,
  getNodeEventCatalog,
} from '../services/api.service';
import {
  EVENT_TYPE_OPTIONS,
  scopeOf,
  eventTypeLabel,
  parseEventTypes,
} from '../constants/alertRules';

// A new rule notifies the Dashboard. Email / Message API are not the rule's: Routing policies decide who gets them.
const blankConfig = () => ({
  name: '', event_type: '*', severity: '*', camera_id: null, group_id: null,
  min_confidence: 0.5, cooldown_seconds: 30, description: '',
  conditions: null, sustain_seconds: 0, message_template: '',
  websocket_enabled: true,
  webhook_enabled: false, webhook_url: '', is_active: true,
});

/** Normalize form → API payload: clamp confidence, coerce numbers, enforce scope exclusivity. */
function normalize(form: any) {
  const scope = scopeOf(form);
  const rule = form;
  return {
    ...rule,
    // Empty selection means "all event types" — persist the wildcard so an empty rule
    // isn't silently dead (an empty event_type matches nothing at evaluation time).
    event_type: parseEventTypes(form.event_type).join(',') || '*',
    camera_id: scope === 'camera' ? form.camera_id : null,
    group_id: scope === 'group' ? form.group_id : null,
    camera_ids: scope === 'cameras' ? form.camera_ids : null,
    min_confidence: Math.min(1, Math.max(0, parseFloat(form.min_confidence) || 0)),
    cooldown_seconds: Math.max(0, parseInt(form.cooldown_seconds, 10) || 0),
    sustain_seconds: Math.max(0, parseInt(form.sustain_seconds, 10) || 0),
    message_template: String(form.message_template || '').trim() || null,
  };
}

/** The message an operator can act on, out of whatever shape the failure arrived in. */
function errorText(err: any, fallback: string): string {
  return (
    err?.response?.data?.error?.message ||
    err?.response?.data?.message ||
    err?.message ||
    fallback
  );
}

/** Embedded = the "AI rules" part of the Alert page's Alert Config tab: no title bar of its own. */
export const EMBEDDED_RULES_H = 'h-[calc(100vh-15rem)] min-h-[24rem]';

/**
 * `siteId` / `siteName`: the site whose rules to show — the Alert page picks a site in its own
 * list. Without them, the title-bar switcher's site (the routed page).
 */
export default function AnalyticsConfigPage({ embedded = false, siteId, siteName, onCounts }: { embedded?: boolean; siteId?: string; siteName?: string; onCounts?: (c: { total: number; on: number }) => void } = {}) {
  const { selectedNode } = useNode();
  const { hasPermission } = useAuth();
  // Creating / editing / toggling / duplicating / deleting alert rules is gated
  // on alerts.config; the backend enforces it too. View stays open to any viewer.
  const canConfig = hasPermission('alerts.config');
  const nodeId = siteId ?? (selectedNode?.nodeId || '');
  const nodeName = siteId ? siteName || siteId : selectedNode?.name || nodeId;

  const [configs, setConfigs] = useState<any[]>([]);
  const [cameras, setCameras] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  // What the node's AI module has actually sent — event types and their fields for the rule form.
  const [catalog, setCatalog] = useState<EventCatalogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');

  // One modal serves both create and edit: `null` = closed, `{ rule: null }` = new rule.
  // Editing used to expand the row into an inline form, which pushed every rule below it off
  // screen and gave a half-finished edit nowhere to live but the list.
  const [formFor, setFormFor] = useState<{ rule: any | null } | null>(null);
  const [deleteFor, setDeleteFor] = useState<any | null>(null);
  // Rules whose enable/disable call is in flight — keyed by id so one pending toggle can't
  // freeze the whole table.
  const [togglingIds, setTogglingIds] = useState<string[]>([]);


  const fetchConfigs = useCallback(async () => {
    if (!nodeId) { setConfigs([]); setLoading(false); return; }
    setLoading(true);
    try {
      setConfigs(await getNodeAlertConfigs(nodeId) || []);
      setPageError('');
    } catch (err) {
      console.error('Failed to fetch configs:', err);
      setPageError(errorText(err, 'Failed to load alert rules'));
    } finally {
      setLoading(false);
    }
  }, [nodeId]);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  /**
   * A rule form (or a delete confirmation) open across a site switch is pointed at a rule id
   * belonging to the site just left — its Save would write to the new node under an id that
   * node has never issued. The rules list refetches above; these have to be closed.
   */
  const firstNodeRef = useRef(true);
  useEffect(() => {
    if (firstNodeRef.current) { firstNodeRef.current = false; return; }
    setFormFor(null);
    setDeleteFor(null);
    setTogglingIds([]);
    setPageError('');
  }, [nodeId]);

  // Cameras + groups feed the scope pickers in the rule form.
  useEffect(() => {
    if (!nodeId) return;
    getNodeCameras(nodeId).then((c: any) => setCameras(Array.isArray(c) ? c : c?.data || [])).catch(() => setCameras([]));
    getNodeGroups(nodeId).then((g: any) => setGroups(Array.isArray(g) ? g : g?.data || [])).catch(() => setGroups([]));
    getNodeEventCatalog(nodeId).then(setCatalog).catch(() => setCatalog([]));
  }, [nodeId]);

  const handleToggle = async (cfg: any) => {
    setTogglingIds((prev) => [...prev, cfg.id]);
    // Optimistic: the switch is the one control an operator flips repeatedly, and waiting a
    // tunnel round trip for it to move reads as a dead toggle. Reverted below if it fails.
    setConfigs((prev) => prev.map((c) => c.id === cfg.id ? { ...c, is_active: !c.is_active } : c));
    try {
      const updated = await toggleNodeAlertConfig(nodeId, cfg.id);
      setConfigs((prev) => prev.map((c) => c.id === cfg.id ? { ...c, is_active: updated.is_active } : c));
      setPageError('');
    } catch (err) {
      console.error('Toggle failed:', err);
      setConfigs((prev) => prev.map((c) => c.id === cfg.id ? { ...c, is_active: cfg.is_active } : c));
      setPageError(errorText(err, `Failed to ${cfg.is_active ? 'disable' : 'enable'} this rule`));
    } finally {
      setTogglingIds((prev) => prev.filter((id) => id !== cfg.id));
    }
  };

  const handleDelete = async () => {
    if (!deleteFor) return;
    try {
      await deleteNodeAlertConfig(nodeId, deleteFor.id);
      setConfigs((prev) => prev.filter((c) => c.id !== deleteFor.id));
      setDeleteFor(null);
      setPageError('');
    } catch (err) {
      console.error('Delete failed:', err);
      setDeleteFor(null);
      setPageError(errorText(err, 'Failed to delete this rule'));
    }
  };

  const handleDuplicate = async (id: string) => {
    try {
      const clone = await duplicateNodeAlertConfig(nodeId, id);
      setConfigs((prev) => [clone, ...prev]);
      setPageError('');
    } catch (err) {
      console.error('Duplicate failed:', err);
      setPageError(errorText(err, 'Failed to duplicate this rule'));
    }
  };

  // Both submit paths throw on failure: the modal stays open and shows why, instead of
  // closing on a save that never happened.
  const handleSubmit = async (data: any) => {
    let saved: any;
    if (formFor?.rule) {
      saved = await updateNodeAlertConfig(nodeId, formFor.rule.id, normalize(data));
      setConfigs((prev) => prev.map((c) => c.id === formFor.rule.id ? saved : c));
    } else {
      saved = await createNodeAlertConfig(nodeId, normalize(data));
      setConfigs((prev) => [saved, ...prev]);
    }
    setPageError('');
    return saved;
  };

  const enabledCount = configs.filter((c: any) => c.is_active).length;
  // The Alert Config header shows how many rules there are (its AI analytics / Watchlist / System switch).
  useEffect(() => { onCounts?.({ total: configs.length, on: enabledCount }); }, [configs.length, enabledCount]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!nodeId) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground p-6">
        Select a site to configure its alert rules.
      </div>
    );
  }

  const subtitle = <>{configs.length} {configs.length === 1 ? 'rule' : 'rules'} · {enabledCount} enabled · Decide which AI detections on <span className="font-medium">{nodeName}</span> become alerts.</>;

  return (
    <div className={`flex flex-col gap-3 ${embedded ? EMBEDDED_RULES_H : PAGE_FILL_H}`}>
      {!embedded && <PageTitle title="Alert Rules" subtitle={subtitle} />}
      <AlertRulesList
        rules={configs}
        loading={loading}
        canConfig={canConfig}
        togglingIds={togglingIds}
        cameras={cameras}
        groups={groups}
        eventTypeLabel={eventTypeLabel}
        onToggle={handleToggle}
        onEdit={(rule: any) => setFormFor({ rule })}
        onDuplicate={handleDuplicate}
        onDelete={setDeleteFor}
        onCreate={() => setFormFor({ rule: null })}
        onRefresh={fetchConfigs}
        error={pageError}
        onDismissError={() => setPageError('')}
      />

      {/* ── Create / edit, same form either way ── */}
      <RuleFormModal
        open={!!formFor}
        rule={formFor?.rule || null}
        cameras={cameras}
        groups={groups}
        catalog={catalog}
        nodeId={nodeId}
        onSubmit={handleSubmit}
        onClose={() => setFormFor(null)}
      />

      <ConfirmDialog
        open={!!deleteFor}
        title="Delete alert rule"
        message={
          <>
            Detections matching <span className="font-medium text-foreground">{deleteFor ? ruleTitle(deleteFor, eventTypeLabel) : ''}</span> will
            no longer raise alerts. Alerts already raised are kept.
          </>
        }
        confirmLabel="Delete rule"
        onConfirm={handleDelete}
        onCancel={() => setDeleteFor(null)}
      />
    </div>
  );
}

// ─── The form ─────────────────────────────────────────────────

/** What can't be saved, said in the operator's terms rather than the API's. */
function validate(form: any): string {
  const scope = scopeOf(form);
  if (scope === 'camera' && !form.camera_id) return 'Pick the camera this rule applies to.';
  if (scope === 'group' && !form.group_id) return 'Pick the group this rule applies to.';
  if (scope === 'cameras' && !(form.camera_ids || []).length) return 'Pick at least one camera.';
  const condProblem = conditionsProblem(form);
  if (condProblem) return condProblem;
  if (form.webhook_enabled && !String(form.webhook_url || '').trim()) return 'Add the webhook URL, or turn Webhook off.';
  return '';
}

/**
 * Create AND edit. One form for both, so the two can't drift, and an edit no longer expands
 * the row it belongs to — which pushed the rest of the table off screen mid-edit.
 */
function RuleFormModal({ open, rule, cameras, groups, catalog, nodeId, onSubmit, onClose }: {
  open: boolean;
  rule: any | null;
  cameras: any[];
  groups: any[];
  catalog: EventCatalogEntry[];
  nodeId: string;
  onSubmit: (data: any) => Promise<void>;
  onClose: () => void;
}) {
  const [form, setForm] = useState<any>(blankConfig);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Bumped on every open: the form remounts, so its own state starts from THIS rule.
  const [formKey, setFormKey] = useState(0);
  // The form as it opened — the dialog says "No changes yet" until it differs.
  const [initialForm, setInitialForm] = useState<any>(null);

  // Reset on open, and whenever the modal is pointed at a different rule.
  useEffect(() => {
    if (!open) return;
    const start = rule ? { ...rule } : blankConfig();
    setForm(start);
    setInitialForm(start);
    setFormKey((k) => k + 1);
    setError('');
    setSaving(false);
  }, [open, rule]);

  const problem = validate(form);

  async function handleSave() {
    if (problem) { setError(problem); return; }
    setSaving(true);
    setError('');
    try {
      await onSubmit(form);
      onClose();
    } catch (err) {
      console.error('Save failed:', err);
      setError(errorText(err, 'Failed to save this rule'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AlertRuleModal
      open={open}
      onClose={onClose}
      isNew={!rule}
      initial={initialForm}
      saving={saving}
      error={error}
      problem={problem}
      onSave={handleSave}
      formKey={formKey}
      form={form} setForm={setForm} cameras={cameras} groups={groups} catalog={catalog || []}
      builtin={EVENT_TYPE_OPTIONS} routingHref={`/alert-actions?tab=actions&site=${encodeURIComponent(nodeId)}`}
    />
  );
}
